/**
 * Окно отбора продаж ОДНОЙ КНОПКОЙ-ДРОПДАУНОМ.
 *
 * Пришло на смену `DateWindow` 11.09.26 (замечание владельца разметкой:
 * «используй нормальный дропдаун-пикер, где можно сразу выбрать период, а не
 * так, как два поля»; «выбор периода сделай комбо-боксом, чтобы тулбар по
 * ширине сократился»). Два поля даты плюс четыре чипа плюс подпись охвата
 * занимали 820 px и ломали тулбар на две строки — из-за этой второй строки
 * таблица размеров стояла на 53 px ниже соседней витрины.
 *
 * ВЫБОР ОКНА И ПРАВКА ДАТ — ОДИН ЭЛЕМЕНТ, а не два рядом. Окно «последние N
 * дней» спрашивают в девяти случаях из десяти, и оно стоит списком; свой
 * период лежит в том же попапе ниже, потому что это тот же вопрос «за какой
 * срок», заданный точнее.
 *
 * ПОДПИСЬ ОХВАТА УШЛА ИЗ ТУЛБАРА В КНОПКУ (замечание владельца: «эта
 * информация не нужна, чтобы она не расширяла вот этот верхний тулбар»).
 * Вопрос «за что эти числа» она отвечать не перестала: точные даты стоят в
 * подсказке кнопки и полями в попапе — там, где их и правят.
 *
 * КАЛЕНДАРЬ СВОЙ И ОБЩИЙ С «ПЛАНАМИ» (`RangeCalendar`) — с 16.09.26, замечание
 * владельца разметкой на `/supplier/3651`: «в периоде выбрано два поля по датам,
 * используй нормальный дейтпикер». Здесь стояло обратное правило — «календарь
 * браузерный, своего не заводим», — и держалось оно ровно до того дня, когда
 * свой календарь уже появился у «Планов» (15.09.26): нативный попап браузер
 * рисует системным окном, в тёмной теме белым, а две разных механики выбора
 * даты в одном кабинете дороже, чем один общий компонент.
 *
 * ПРЕСЕТ НЕ ХРАНИТ СВОЕГО СОСТОЯНИЯ: он лишь ставит пару дат, а подсвечен тот,
 * чьи даты совпали с нынешними. Второе состояние разъехалось бы с первым в тот
 * момент, когда человек поправит дату руками.
 *
 * СЕЗОНЫ — ТРЕТЬЯ ГРУППА ПОПАПА (16.09.26, просьба владельца): четыре квартала
 * с листаемым годом, между списком окон и календарём. Список скользящих окон
 * при этом остался (решение владельца): «60 дней» — дефолт трёх экранов сразу,
 * и заменить его сезоном значило бы сменить дефолт всему кабинету.
 *
 * Пикер ОДИН на кабинет, поэтому сезоны приезжают разом в окно продаж карточки,
 * в шестерёнку ленты склейки и в тулбар раздела «Товары» — включая просьбу «в
 * разделе „Товары“ тоже эту логику внедрим».
 */

import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RangeCalendar } from "@/components/RangeCalendar";
import { iso, lastDays, seasonOf, seasons } from "@/lib/dates";

/** Окна, которые спрашивают чаще всего. 60 дней — дефолт всего экрана
 * (15.09.26, до этого 90), и считает его СЕРВЕР: здесь эта цифра только
 * подсвечивает строку. Без пресета под дефолт кнопка печатала бы вместо имени
 * пару дат. */
export type PeriodPreset = { days: number; label: string; hint: string };

const PRESETS: readonly PeriodPreset[] = [
  { days: 30, label: "30 дней", hint: "последний месяц" },
  {
    days: 60,
    label: "60 дней",
    hint: "последние два месяца — окно по умолчанию, то же, что у уходимости",
  },
  { days: 90, label: "90 дней", hint: "последние три месяца" },
  { days: 365, label: "год", hint: "последние двенадцать месяцев" },
] as const;

/** Дата под подпись: день и месяц без года — в кнопке год только шумит, а обе
 * границы почти всегда в одном году. Полная дата живёт в подсказке. */
function краткая(value: string): string {
  const [, m, d] = value.split("-");
  return m && d ? `${d}.${m}` : value;
}

export function PeriodPicker({
  from,
  to,
  since,
  days,
  onChange,
  hint,
  label = "период",
  presets = PRESETS,
  annot = "окно продаж",
  max,
}: {
  /** Начало окна, `YYYY-MM-DD`: с сервера, а не из адреса — адрес пуст, пока
   * человек ничего не выбрал. */
  from: string;
  to: string;
  /** Первый день, за который у товара вообще есть движения финотчёта, — из
   * `cover`. Им живёт строка «всё»; `null` — данных нет вовсе. */
  since: string | null;
  /** Длина нынешнего окна в днях, с сервера: по ней подсвечивается пресет,
   * даже когда адрес пуст и дат в нём нет. */
  days: number;
  onChange: (from: string, to: string) => void;
  /** Подсказка кнопки вместо «продажи за …»: в разделе «Товары» период задаёт
   *  числа плитки, а не продажи. */
  hint?: string;
  /** Имя в серой плашке кнопки. Тот же пикер задаёт и окно УХОДИМОСТИ
   *  «Товаров» (разметка 26.09.26 п.2: «такой же date picker, как и период»). */
  label?: string;
  /** Готовые окна «последние N дней». */
  presets?: readonly PeriodPreset[];
  annot?: string;
  /** Последний день календаря включительно; без него выбор не ограничен. */
  max?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Сезон, которому отвечает нынешнее окно: им подсвечена строка и подписана
  // кнопка. Своего состояния сезон, как и пресет, не хранит — второе состояние
  // разъехалось бы с первым при первой же правке дат руками.
  const сезон = seasonOf(from, to);
  const нынешний = new Date().getFullYear();
  const [год, setГод] = useState(() => сезон?.year ?? нынешний);

  // Попап открывается НА ГОДУ ВЫБРАННОГО СЕЗОНА: вернувшись за соседним
  // кварталом, человек не должен искать год заново. Окно не сезонное — текущий.
  useEffect(() => {
    if (open) setГод(сезон?.year ?? нынешний);
    // Год пересчитывается по открытию, а не по каждой правке окна: иначе
    // пролистанный назад год возвращался бы к текущему на первом же клике.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Клик мимо закрывает список. Слушаем mousedown, а не click: щелчок по
  // соседней кнопке отбора должен и закрыть список, и сработать сам.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  // Escape закрывает — как у соседнего `Combo`: попап перекрывает таблицу, и
  // уйти из него клавишей человек ждёт по привычке от всего кабинета.
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [open]);

  // Пресет подсвечен, когда окно кончается СЕГОДНЯ и длиной совпало: окно той
  // же длины, но месячной давности, — это уже не «последние 30 дней».
  const свежее = !to || to === iso(new Date());
  const пресет = свежее ? presets.find((it) => it.days === days) : undefined;
  const всё = Boolean(свежее && since && from === since);

  // Что написано на кнопке. Узнанное окно зовётся своим именем, чужое —
  // парой дат: «60 дней» и «18.07 — 15.09» отвечают на один вопрос, но первое
  // короче, а тулбар мы как раз сокращаем.
  // Узнанное окно зовётся своим именем: сезон — «июль — сентябрь», а год у него
  // печатается, только когда он не текущий (иначе кнопка шумит числом, которое
  // и так подразумевается).
  const значение = всё
    ? "всё"
    : пресет
      ? пресет.label
      : сезон
        ? сезон.year === нынешний
          ? сезон.label
          : `${сезон.label} ${сезон.year}`
        : `${краткая(from)} — ${краткая(to)}`;

  const выбрать = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative" data-annot={annot}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={hint ?? `продажи за ${from} … ${to} — ${days} дней`}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open
            ? "border-border-strong"
            : "border-border hover:border-border-strong")
        }
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-muted">
          {label}
        </span>
        <span className="truncate px-2 tabular-nums text-fg">{значение}</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-muted" />
      </button>

      {open ? (
        // Попап: Enter на его кнопке нажимает кнопку, а не применяет тулбар «Товаров».
        <div
          className="absolute top-[34px] left-0 z-30 w-[264px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg"
          data-popup=""
        >
          <div className="py-1">
            {presets.map((preset) => (
              <Строка
                key={preset.days}
                label={preset.label}
                title={preset.hint}
                on={!всё && пресет?.days === preset.days}
                onClick={() => выбрать(...lastDays(preset.days))}
              />
            ))}
            {/* «Всё» — от первого движения товара до сегодня. Опирается на
                `cover`, а не на пустой адрес: пустой отдаёт дефолтные 60 дней,
                и «всё» показало бы ими же. Данных нет вовсе — строки нет: она
                обещала бы историю, которой не существует. */}
            {since && (
              <Строка
                label="всё"
                meta={`с ${краткая(since)}.${since.slice(0, 4)}`}
                title={`вся жизнь товара в отчёте — с ${since}`}
                on={всё}
                onClick={() => выбрать(since, iso(new Date()))}
              />
            )}
          </div>

          {/* СЕЗОНЫ — КВАРТАЛЫ, и год у них листается стрелками (16.09.26,
              просьба владельца: «январь, март, апрель, июнь, июль, сентябрь,
              октябрь, декабрь — это наши сезоны»). Без выбора года кнопка была
              бы мёртвой четверть года: при сегодня в сентябре квартал «октябрь
              — декабрь» текущего года ещё не начался. Вперёд дальше текущего
              года не ходим — там не начался ни один сезон. */}
          <div className="border-t border-border pt-1" data-annot="сезоны">
            <div className="flex items-center gap-1 px-2.5 pb-1 text-[11.5px] text-fg-muted">
              <span>сезоны</span>
              <span className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  title="год назад"
                  onClick={() => setГод((y) => y - 1)}
                  className="grid h-5 w-5 place-items-center rounded-[4px] hover:bg-bg-hover"
                >
                  <ChevronLeft size={12} />
                </button>
                <span className="tabular-nums text-fg">{год}</span>
                <button
                  type="button"
                  title="год вперёд"
                  disabled={год >= нынешний}
                  onClick={() => setГод((y) => y + 1)}
                  className="grid h-5 w-5 place-items-center rounded-[4px] hover:bg-bg-hover disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronRight size={12} />
                </button>
              </span>
            </div>
            {/* Не начавшийся сезон — СТРОКА БЕЗ КЛИКА, а не пропуск: пустой
                экран на клик хуже серой строки, которая называет причину. */}
            {seasons(год).map((s) =>
              s.started ? (
                <Строка
                  key={s.key}
                  label={s.label}
                  title={`${s.from} … ${s.to}`}
                  on={сезон?.key === s.key}
                  onClick={() => выбрать(s.from, s.to)}
                />
              ) : (
                <div
                  key={s.key}
                  title="сезон ещё не начался"
                  className="px-2.5 py-1.5 text-[12.5px] text-fg-muted opacity-50"
                >
                  {s.label}
                </div>
              ),
            )}
          </div>

          {/* СВОЙ ПЕРИОД ЛЕЖИТ В ТОМ ЖЕ ПОПАПЕ, а не рядом с кнопкой: это тот
              же вопрос «за какой срок», заданный точнее, и тулбар он больше не
              расширяет. Отбор применяется ВТОРЫМ КЛИКОМ календаря, без кнопки
              «применить»: второе состояние (черновик дат против отбора)
              разъехалось бы с первым. */}
          <div className="border-t border-border">
            <div className="px-2.5 pt-2 text-[11.5px] text-fg-muted">свой период</div>
            <RangeCalendar from={from} to={to} onPick={выбрать} max={max} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Строка списка окон. Галочка слева отсутствует у невыбранных, но место под
 *  неё держится: строки иначе разъезжались бы влево-вправо при выборе.
 *  Экспортирована для `WindowPicker`: два списка одного тулбара, разойдясь в
 *  отступах, читались бы как чужие элементы. */
export function Строка({
  label,
  meta,
  title,
  on,
  onClick,
}: {
  label: string;
  meta?: string;
  title: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={
        "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-bg-hover " +
        (on ? "text-accent-text" : "text-fg")
      }
    >
      <span className="truncate">{label}</span>
      {meta ? (
        <span className="tnum ml-auto flex-none pl-2 text-[11.5px] text-fg-muted">
          {meta}
        </span>
      ) : null}
      {on ? <Check size={12} className={meta ? "flex-none" : "ml-auto flex-none"} /> : null}
    </button>
  );
}
